import api from './base-api';
import {
	MarkNotificationReadDto,
	CreateSelfNotificationDto,
	CreateNotificationForEventDto,
} from '@sportspredict/shared';

interface CreateNotificationForEventRequest extends CreateNotificationForEventDto {
	event_id: string;
}

const notificationsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		logClient: builder.mutation({
			query: (body: any) => ({
				url: '/notifications/log-client',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
		}),
		getNotifications: builder.query({
			query: ({ event_id }: { event_id: string }) => ({
				url: 'notifications',
				method: 'GET',
				params: { event_id },
			}),
			providesTags: [{ type: 'Notification' as const, id: 'LIST' }],
		}),
		getNotificationById: builder.query({
			query: (id: string) => ({
				url: `notifications/${id}`,
				method: 'GET',
			}),
		}),
		getNotificationsList: builder.query({
			query: () => ({
				url: 'notifications/list',
				method: 'GET',
			}),
			providesTags: [{ type: 'Notification' as const, id: 'LIST' }],
		}),
		getListOpenMarket: builder.query({
			query: (event_id: string) => ({
				url: `notifications/list-open-market/${event_id}`,
				method: 'GET',
			}),
			providesTags: [{ type: 'Notification' as const, id: 'OPEN_MARKET' }],
		}),
		createNotificationForMyself: builder.mutation<unknown, CreateSelfNotificationDto>({
			query: (body) => ({
				url: 'notifications/self',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
		}),
		createNotificationForEvent: builder.mutation<unknown, CreateNotificationForEventRequest>({
			query: ({ event_id, ...body }) => ({
				url: `notifications/event/${encodeURIComponent(event_id)}`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
		}),
		updateNotification: builder.mutation({
			query: ({ id, ...data }: { id: string; [key: string]: any }) => ({
				url: 'notifications',
				method: 'PUT',
				body: { id, ...data },
			}),
			invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
		}),
		markNotificationAsRead: builder.mutation<unknown, MarkNotificationReadDto>({
			query: (body) => ({
				url: 'notifications/read',
				method: 'PUT',
				body,
			}),
			invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
		}),
		deleteNotification: builder.mutation({
			query: (id: string) => ({
				url: `notifications/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'Notification', id: 'LIST' }],
		}),
		subscribeToOpenMarket: builder.mutation({
			query: (match_id: string) => ({
				url: `notifications/subscribe-open-market/${match_id}`,
				method: 'POST',
			}),
			invalidatesTags: [{ type: 'Notification', id: 'OPEN_MARKET' }],
		}),
	}),
	overrideExisting: false,
});

export const { useLogClientMutation } = notificationsApi;

export const {
	useGetNotificationsQuery,
	useLazyGetNotificationsQuery,
	useGetNotificationByIdQuery,
	useLazyGetNotificationByIdQuery,
	useGetNotificationsListQuery,
	useLazyGetNotificationsListQuery,
	useGetListOpenMarketQuery,
	useCreateNotificationForMyselfMutation,
	useCreateNotificationForEventMutation,
	useUpdateNotificationMutation,
	useMarkNotificationAsReadMutation,
	useDeleteNotificationMutation,
	useSubscribeToOpenMarketMutation,
} = notificationsApi;
